import NewFileUploader from '@components/NewFileUploader';
import { ErrorMessage, TaAutocomplete, TaInput } from '@components/shared';
import { GeneralMuiModal } from '@components/shared/general-mui-modal';
import { useMapsStore } from '@components/shared/Maps/store';
import { TaMaps } from '@components/shared/Maps/TaMaps';
import { useGeolocated } from '@components/shared/Maps/useGeoLocated';
import { useCurrentAddress } from '@components/shared/Maps/utils';
import { useSnackbarStore } from '@components/shared/Snackbar/store';
import { theme } from '@config/theme';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import { useModalDimensions } from '@hooks/useModalDimensions';
import { usePageTitle } from '@hooks/usePageTitle';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import { Checkbox, FormControlLabel } from '@mui/material';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import { useDeleteFarmFile, useFarmStore, useSingleFarm, useUpdateFarm } from '@pages/MyFarm/store';
import { Dictionary, Nullable } from '@utils/util.types';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useCrops } from 'services/crops';
import { useIrrigationStore, useIrrigationTypes } from 'services/irrigations';
import * as Yup from 'yup';

interface FarmFile {
	id?: string;
	file?: string;
	fileType?: string;
	s3_key?: string;
	filename?: string;
}

interface CropInfo {
	crop_latitude: number;
	crop_longitude: number;
	crop_location_name: string;
	planted: boolean;
	weather_station: boolean;
	irrigation_water: boolean;
	irrigation_system: boolean;
	irrigation_system_id: Nullable<Dictionary>;
	sensors: boolean;
	management_soft: boolean;
	greenhouse: boolean;
	soil_analysis: boolean;
	soil_analysis_file: Nullable<string>;
	farm_files?: Array<FarmFile>;
}

const validationSchema = Yup.object({
	crop_location_name: Yup.string(),
	crop_latitude: Yup.number(),
	crop_longitude: Yup.number(),
	planted: Yup.boolean(),
	weather_station: Yup.boolean(),
	irrigation_water: Yup.boolean(),
	irrigation_system: Yup.boolean(),
	irrigation_system_id: Yup.mixed().when('irrigation_system', {
		is: true,
		then: (schema) => schema.required('myFarm.irrigationSystemValidation'),
		otherwise: (schema) => schema.nullable(),
	}),
	sensors: Yup.boolean(),
	management_soft: Yup.boolean(),
	greenhouse: Yup.boolean(),
	soil_analysis: Yup.boolean(),
	soil_analysis_file: Yup.mixed().when('soil_analysis', {
		is: true,
		then: (schema) => schema.required('myFarm.soil_analysis_file'),
		otherwise: (schema) => schema.nullable(),
	}),
});

const buttonStyle = {
	backgroundColor: theme.palette.custom.contrastTextWhite,
	color: theme.palette.custom.darkGray,
};

const initialValues: CropInfo = {
	crop_latitude: 0,
	crop_longitude: 0,
	crop_location_name: '',
	planted: false,
	weather_station: false,
	irrigation_water: false,
	irrigation_system: false,
	irrigation_system_id: null,
	sensors: false,
	soil_analysis: false,
	management_soft: false,
	greenhouse: false,
	soil_analysis_file: null,
	farm_files: [],
};

const checkboxFields: { label: string; name: keyof CropInfo }[] = [
	{ label: 'planted', name: 'planted' },
	{ label: 'weather_station', name: 'weather_station' },
	{ label: 'irrigation_water', name: 'irrigation_water' },
	{ label: 'irrigation_system', name: 'irrigation_system' },
	{ label: 'sensors', name: 'sensors' },
	{ label: 'management_soft', name: 'management_soft' },
	{ label: 'greenhouse', name: 'greenhouse' },
	{ label: 'soil_analysis', name: 'soil_analysis' },
];

const allowedFormats = [
	'.txt',
	'.doc',
	'.docx',
	'.pdf',
	'.xls',
	'.xlsx',
	'.ppt',
	'.kmz',
	'.kml',
	'.jpg',
	'.jpeg',
	'.png',
	'.gif',
	'.heic',
	'.webp',
	'.heif',
];

export function CropAdditionalInformation({ showActions = true }: { showActions?: boolean }) {
	usePageTitle(`My Farm`);
	const modalDimensions = useModalDimensions();
	const { t } = useTranslation();
	const { coords } = useGeolocated();
	const { address, loading } = useCurrentAddress();
	const { navigateWithLanguage } = useLanguageNavigate();
	const { update } = useUpdateFarm();
	const { irrigation } = useIrrigationStore();
	const { id } = useParams();
	const localFarmId = localStorage.getItem('farmId') || '';
	const farmId = id || localFarmId;
	const [mapOpen, setMapOpen] = useState(false);
	const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
	const { setAddress } = useMapsStore();
	const openMapsModal = () => setMapOpen(true);
	const closeMapsModal = () => {
		setMapOpen(false);
	};
	const [uploadedFiles, setUploadedFiles] = useState<Array<{ file: string; id?: string }>>([]);
	const { singleFarm } = useFarmStore();
	const { remove } = useDeleteFarmFile();
	const { refresh } = useSingleFarm(farmId || '');
	const { pathname } = useLocation();
	const isEdit = useMemo(() => pathname.includes('edit'), [pathname]);
	const position = useMemo(
		() => ({
			lat: coords?.latitude || 0,
			lng: coords?.longitude || 0,
		}),
		[coords]
	);

	useIrrigationTypes();
	useCrops();

	const formik = useFormik<CropInfo>({
		initialValues,
		validationSchema,
		onSubmit: async (values) => {
			const formattedValues = {
				...values,
				irrigation_system_id: values.irrigation_system_id?.id,
				soil_analysis: undefined,
				farm_files: values.farm_files,
				soil_analysis_file: undefined,
			};

			try {
				await update(formattedValues as any, farmId as string);
				localStorage.removeItem('farmId');
				setAddress('');

				navigateWithLanguage('/farmer/my-farm');
				if (isEdit) {
					showSnackbar(
						t('myFarm.updateSnackbarTitle'),
						t('myFarm.updateSnackbarDescription'),
						'success'
					);
				} else {
					showSnackbar(
						t('myFarm.createSnackbarTitle'),
						t('myFarm.createSnackbarDescription'),
						'success'
					);
				}
			} catch (error) {
				showSnackbar('Error', 'Something went wrong. Please try again.', 'error');
			}
		},
	});
	const { setValues, setFieldValue } = formik;

	const handlePrev = () => {
		if (farmId) {
			navigateWithLanguage(`/farmer/my-farm/edit/${farmId}/crop-selection`);

			return;
		}
		navigateWithLanguage('/farmer/my-farm/create/crop-selection');
	};

	useEffect(() => {
		if (singleFarm) {
			setValues({
				crop_latitude: parseFloat(singleFarm.crop_latitude) || (position?.lat as number),
				crop_longitude: parseFloat(singleFarm.crop_longitude) || (position?.lng as number),
				crop_location_name: singleFarm.crop_location_name || address,
				planted: singleFarm.planted,
				weather_station: singleFarm.weather_station,
				irrigation_water: singleFarm.irrigation_water,
				irrigation_system: singleFarm.irrigation_system,
				irrigation_system_id: singleFarm.irrigationSystem || '',
				sensors: singleFarm.sensors,
				management_soft: singleFarm.management_soft,
				greenhouse: singleFarm.greenhouse,
				soil_analysis: singleFarm.farm_files?.length > 0,
				soil_analysis_file:
					singleFarm.farm_files?.length > 0 ? singleFarm.farm_files[0].filename : '',
				farm_files: singleFarm.farm_files || [],
			});

			if (singleFarm.farm_files?.length > 0) {
				const files = singleFarm.farm_files.map((file: FarmFile) => ({
					file: file.s3_key || '',
					id: file.id || '',
				}));
				setUploadedFiles(files);
			}
		}
	}, [singleFarm, address, setFieldValue, position, setValues]);

	const handleLocationSelect = (address: string, lat: number, lng: number) => {
		setFieldValue('crop_location_name', address);
		setFieldValue('crop_latitude', lat);
		setFieldValue('crop_longitude', lng);
	};

	const handleFileRemove = async (fileId: string) => {
		await remove(fileId);
		await refresh();
		showSnackbar(t('fileRemoved'), t('fileRemovedSuccessfully'), 'success');
	};

	const handleNewFileUpload = async (status: any) => {
		if (!status || !status.key) return;

		const newFileEntry = { file: status.key };

		setUploadedFiles((prev) => [...prev, newFileEntry]);

		if (status.key) {
			setValues({
				...formik.values,
				soil_analysis_file: status.fileName,
			});

			const formattedValues = {
				...formik.values,
				irrigation_system_id: formik.values.irrigation_system_id?.id,
				soil_analysis: undefined,
				farm_files: [...uploadedFiles, newFileEntry],
				soil_analysis_file: undefined,
			};

			try {
				await update(formattedValues, farmId as string);
				await refresh();
			} catch (error) {
				console.error('Error updating farm with new file:', error);
				showSnackbar('Error', 'Failed to update farm with uploaded file.', 'error');
			}
		}
	};

	const handleNewFileUploaderRemove = async (id: string) => {
		await handleFileRemove(id);
		setUploadedFiles((prev) => prev.filter((item) => item.id !== id));
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;

		formik.handleChange(e);
		if (name === 'irrigation_system' && !checked) {
			formik.setFieldValue('irrigation_system_id', null);
			formik.setFieldError('irrigation_system_id', undefined); // Clear the error
			formik.setFieldTouched('irrigation_system_id', false); // Reset touched state
		}
		if (name === 'soil_analysis' && !checked) {
			formik.setFieldValue('soil_analysis_file', null);
			formik.setFieldError('soil_analysis_file', undefined); // Clear the error
			formik.setFieldTouched('soil_analysis_file', false); // Reset touched state
		}
	};
	return (
		<>
			<div className="flex flex-col items-center h-full gap-[20px]  mt-[10px] sm:mt-0 p-[20px] relative">
				<form
					onSubmit={formik.handleSubmit}
					className="flex flex-col items-center justify-center  xs:mt-0 h-full gap-[20px] sm:p-[20px] p-0 relative justify"
				>
					<div className="flex flex-col items-center justify-center">
						<div className="w-full xs:w-2/3 p-[20px]">
							{showActions && (
								<h6 className="text-dark text-center font-bold text-xl leading-normal mb-[20px]">
									{t('myFarm.locationLabel')}
								</h6>
							)}
							{loading ? (
								<Skeleton
									variant="rectangular"
									width="100%"
									sx={{
										borderRadius: '8px',
									}}
									height={42}
								/>
							) : (
								<TaInput
									click={openMapsModal}
									endIcon={<FmdGoodOutlinedIcon />}
									id="crop_location_name"
									readonly={true}
									label={t('myFarm.farmLocation')}
									size="small"
									value={formik.values.crop_location_name as string}
									onChange={formik.handleChange}
									error={formik.errors.crop_location_name}
									touched={formik.touched.crop_location_name}
								/>
							)}
						</div>
						<div className="flex flex-col md:flex-row  sm:mt-[40px] mt-0">
							<div className="w-full md:w-1/2  px-4">
								{/*{checkboxFields.slice(0, 4).map(({ label, name }) => (*/}
								{checkboxFields.slice(0, 4).map(({ label, name }) => (
									<div key={name}>
										<FormControlLabel
											disabled={loading}
											control={
												<Checkbox
													checked={!!formik.values[name]}
													onChange={handleCheckboxChange}
													name={name}
													sx={{ paddingTop: 0 }}
												/>
											}
											sx={{ display: 'flex', alignItems: 'flex-start' }}
											label={
												<span className="text-secondary font-roboto text-base !font-normal leading-normal">
													{t(`myFarm.${label}`)}
												</span>
											}
										/>
									</div>
								))}
								{formik.values.irrigation_system && (
									<div className="w-full  md:pr-[10px] my-[20px] sm:my-[8px] mx-0 sm:mx-[33px]">
										<TaAutocomplete
											options={irrigation}
											getOptionLabel={(option) => option.name}
											label={t('myFarm.irrigation_system_id')}
											value={formik.values.irrigation_system_id || null}
											onChange={(_, value) => formik.setFieldValue('irrigation_system_id', value)}
											error={Boolean(
												formik.touched.irrigation_system_id && formik.errors.irrigation_system_id
											)}
										/>
										{formik.touched.irrigation_system_id && formik.errors.irrigation_system_id && (
											<ErrorMessage
												message={t(formik.errors.irrigation_system_id as string, {
													fieldName: t('irrigation_system_id'),
												})}
											/>
										)}
									</div>
								)}
							</div>

							<div className="w-full md:w-1/2 px-4">
								{checkboxFields.slice(4).map(({ label, name }) => (
									<div key={name}>
										<FormControlLabel
											disabled={loading}
											control={
												<Checkbox
													checked={!!formik.values[name]}
													onChange={formik.handleChange}
													name={name}
													sx={{ paddingTop: 0 }}
												/>
											}
											sx={{ display: 'flex', alignItems: 'flex-start' }}
											label={
												<span className="text-secondary font-roboto text-base !font-normal leading-normal">
													{t(`myFarm.${label}`)}
												</span>
											}
										/>
									</div>
								))}
								{formik.values.soil_analysis && (
									<>
										<NewFileUploader
											onExistingFileRemove={handleNewFileUploaderRemove}
											showRemoveButton={true}
											onUploaded={handleNewFileUpload}
											title={t('myFarm.attach_soil_analysis')}
											description={t('myFarm.attach_soil_analysis')}
											allowedFormats={allowedFormats}
											multiple={false}
											existingFiles={singleFarm?.farm_files?.map((file: FarmFile) => ({
												id: file.id || '',
												fileType: file.fileType || 'document',
												s3_key: file.s3_key || '',
												filename: file.filename || '',
											}))}
										/>

										{formik.touched.soil_analysis_file && formik.errors.soil_analysis_file && (
											<ErrorMessage
												message={t(formik.errors.soil_analysis_file as string, {
													fieldName: t('soil_analysis_file'),
												})}
											/>
										)}
									</>
								)}
							</div>
						</div>
					</div>
				</form>
				<div className="flex items-center justify-center md:hidden">
					<p className="text-secondary text-center font-normal text-base">
						{t('myFarm.considerationText')}
					</p>
				</div>
				<div className="w-full  flex items-end justify-between">
					<Button
						onClick={handlePrev}
						classes={{
							root: 'w-[111px]',
						}}
						sx={buttonStyle}
						variant="contained"
						startIcon={<ArrowBackIosIcon />}
					>
						{t('previous')}
					</Button>
					<div className="flex items-center gap-[20px]">
						<p className="text-secondary text-center font-normal text-base md:block hidden">
							{t('myFarm.considerationText')}
						</p>
						<Button
							disabled={loading}
							onClick={() => formik.handleSubmit()}
							classes={{
								root: 'w-[111px]',
							}}
							variant="contained"
							endIcon={<ArrowForwardIosIcon />}
						>
							{t('save')}
						</Button>
					</div>
				</div>
			</div>
			<GeneralMuiModal
				maxWidth={'xl'}
				title={t('myFarm.farmLocation')}
				styles={{
					paperStyle: { width: modalDimensions.width, height: modalDimensions.height, padding: 0 },
					contentStyle: { padding: 0 },
				}}
				open={mapOpen}
				onClose={closeMapsModal}
				content={
					<>
						<TaMaps
							onSave={() => {
								return setMapOpen(false);
							}}
							currentAddress={formik.values.crop_location_name}
							marker={{
								lat: Number(formik.values.crop_latitude),
								lng: Number(formik.values.crop_longitude),
							}}
							onLocationSelect={handleLocationSelect}
						/>
					</>
				}
			/>
		</>
	);
}
